/* ============================================================
   SOLIA v7 — shared hooks and primitive components
   Exports to window so other JSX files can use them.
   ============================================================ */
const { useState, useEffect, useRef, useMemo, useCallback } = React;
/* ---------- site links (standalone defaults; Shopify liquid overrides) ---------- */
window.SOLIA_LINKS = window.SOLIA_LINKS || {
    home: 'Solia v7.html',
    shop: 'Solia Shop.html',
    story: 'Solia Our Story.html',
    science: 'Solia Science.html',
    flavors: 'Solia Flavors.html',
    reviews: 'Solia Reviews.html',
    coconutLime: 'Solia Product Coconut Lime.html',
    pog: 'Solia Product Passion Orange Guava.html',
    contact: 'Solia Contact.html',
    shippingReturns: 'Solia Shipping - Returns.html',
    wholesale: 'Solia Wholesale.html',
    affiliates: 'Solia Affiliates.html',
    faq: 'Solia FAQ.html',
    glp1: 'Solia For GLP-1 Users.html',
    peptides: 'Solia For Peptide Users.html',
    cart: 'Solia Cart.html',
    login: '#',
    account: '#',
    accountHub: 'Solia Account Hub.html',
    subscriptions: 'Solia Subscriptions.html',
    privacy: '#',
    terms: '#',
    /* ---- hidden SEO / conversion spokes (not in nav or footer) ---- */
    quiz: 'Solia GLP-1 Support Quiz.html',
    drinkMix: 'Solia GLP-1 Support Drink Mix.html',
    supplement: 'Solia Supplement for GLP-1 Users.html',
    hydration: 'Solia Hydration Support.html',
    digestive: 'Solia Digestive Support.html',
    nutritionLower: 'Solia Nutrition Support Lower Appetite.html',
    whatToDrink: 'Solia What to Drink on GLP-1s.html',
    vsDiy: 'Solia vs DIY Stack.html',
    companionComplex: 'Solia Companion Complex.html',
    qualityTesting: 'Solia Quality Testing.html',
    supplementFacts: 'Solia Supplement Facts.html',
    safetyAllergen: 'Solia Safety Allergen.html',
    varietyPack: 'Solia Shop.html',
    /* ---- phase 2 SEO spokes ---- */
    startChecklist: 'Solia Starting GLP-1 Nutrition Checklist.html',
    starterKit: 'Solia GLP-1 Starter Kit Guide.html',
    maintenance: 'Solia GLP-1 Maintenance Support.html',
    morningRoutine: 'Solia Morning Routine GLP-1.html',
    travelRoutine: 'Solia Travel Routine GLP-1.html',
    semaglutide: 'Solia Semaglutide Nutrition Support.html',
    tirzepatide: 'Solia Tirzepatide Nutrition Support.html',
    peptideSupport: 'Solia Peptide Weight-Loss Support.html',
    electrolytes: 'Solia Electrolytes Coconut Water.html',
    fiberMagnesium: 'Solia Prebiotic Fiber Magnesium.html',
    creatineAminos: 'Solia Creatine Amino Acids.html',
    collagenSkin: 'Solia Collagen Vitamin C Skin.html',
    bVitamins: 'Solia B-Vitamins Energy Metabolism.html',
    vsElectrolyte: 'Solia vs Electrolyte Drinks.html',
    vsProtein: 'Solia vs Protein Shakes.html',
};
const L = window.SOLIA_LINKS;
/* ---------- hooks ---------- */
function useReveal() {
    useEffect(() => {
        const io = new IntersectionObserver(entries => {
            entries.forEach(e => {
                if (e.isIntersecting) {
                    e.target.classList.add('in');
                    io.unobserve(e.target);
                }
            });
        }, { threshold: 0.12, rootMargin: '-30px 0px' });
        document.querySelectorAll('.rev:not(.in)').forEach(el => io.observe(el));
        return () => io.disconnect();
    });
}
function useScrolled(threshold = 40) {
    const [s, setS] = useState(false);
    useEffect(() => {
        const onS = () => setS(window.scrollY > threshold);
        window.addEventListener('scroll', onS, { passive: true });
        onS();
        return () => window.removeEventListener('scroll', onS);
    }, [threshold]);
    return s;
}
function useScrollY() {
    const [y, setY] = useState(0);
    useEffect(() => {
        let raf = null;
        const update = () => { raf = null; setY(window.scrollY); };
        const onS = () => { if (raf)
            return; raf = requestAnimationFrame(update); };
        window.addEventListener('scroll', onS, { passive: true });
        update();
        return () => { window.removeEventListener('scroll', onS); if (raf)
            cancelAnimationFrame(raf); };
    }, []);
    return y;
}
// scroll progress 0..1 through a ref'd element
function useElementProgress(ref) {
    const [p, setP] = useState(0);
    useEffect(() => {
        if (!ref.current)
            return;
        let raf = null;
        const update = () => {
            raf = null;
            const el = ref.current;
            if (!el)
                return;
            const r = el.getBoundingClientRect();
            const total = r.height - window.innerHeight;
            setP(Math.max(0, Math.min(1, -r.top / Math.max(total, 1))));
        };
        const onS = () => { if (raf)
            return; raf = requestAnimationFrame(update); };
        window.addEventListener('scroll', onS, { passive: true });
        window.addEventListener('resize', onS);
        update();
        return () => {
            window.removeEventListener('scroll', onS);
            window.removeEventListener('resize', onS);
            if (raf)
                cancelAnimationFrame(raf);
        };
    }, []);
    return p;
}
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const lerp = (a, b, t) => a + (b - a) * t;
const range = (x, a, b) => clamp((x - a) / Math.max((b - a), 1e-6), 0, 1);
const ease = t => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
/* ---------- Logo ---------- */
function Logo({ cream = false, height = 42, style = {} }) {
    return (React.createElement("img", { src: cream ? ((window.SOLIA_ASSETS && window.SOLIA_ASSETS.logoCream) || '') : ((window.SOLIA_ASSETS && window.SOLIA_ASSETS.logoDark) || ''), alt: "Solia", style: { height, width: 'auto', display: 'block', ...style } }));
}
/* ---------- Cart + account icons ---------- */
function CartIcon({ size = 21 }) {
    return (React.createElement("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "none", "aria-hidden": "true" },
        React.createElement("path", { d: "M6 7h12l-1 12.5a1.5 1.5 0 0 1-1.5 1.4H8.5A1.5 1.5 0 0 1 7 19.5L6 7Z", stroke: "currentColor", strokeWidth: "1.5", strokeLinejoin: "round" }),
        React.createElement("path", { d: "M9 9V6a3 3 0 0 1 6 0v3", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" })));
}
function AccountIcon({ size = 20 }) {
    return (React.createElement("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "none", "aria-hidden": "true" },
        React.createElement("circle", { cx: "12", cy: "8", r: "3.4", stroke: "currentColor", strokeWidth: "1.5" }),
        React.createElement("path", { d: "M5.5 19.5a6.5 6.5 0 0 1 13 0", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" })));
}
/* ---------- Nav ----------
   Layout: [ Shop + links ]   ·   [ centered logo ]   ·   [ Sign in · Cart ]
   All hrefs optional. Homepage passes page-file links; Shopify sections pass
   live routes (/pages/..., /account/login, /cart) + cartCount from Liquid. */
function Nav({ links, activeHref, homeHref = '#', shopHref = '#shop', shopLabel = 'Shop Now', shopActive = false, signInHref = '#', cartHref = '#', cartCount = 0, accountLabel = 'Sign In' }) {
    const scrolled = useScrolled(40);
    const [menuOpen, setMenuOpen] = useState(false);
    useEffect(() => {
        document.body.style.overflow = menuOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [menuOpen]);
    const navLinks = links || [
        { href: '#story', label: 'Our story' },
        { href: '#science', label: 'Science' },
        { href: '#flavors', label: 'Flavors' },
        { href: '#reviews', label: 'Reviews' },
    ];
    return (React.createElement("nav", { className: `nav ${scrolled ? 'scrolled' : ''}` },
        React.createElement("div", { className: "nav-left" },
            React.createElement("a", { href: shopHref, className: `btn ${shopActive ? 'btn-primary' : 'btn-blue'} nav-shop` }, shopLabel),
            React.createElement("div", { className: "nav-links" }, navLinks.map(l => (React.createElement("a", { key: l.label, href: l.href, className: `nav-link ${activeHref === l.href ? 'active' : ''}` }, l.label))))),
        React.createElement("a", { href: homeHref, className: "nav-logo", "aria-label": "Solia \u2014 home" },
            React.createElement(Logo, { height: 42 })),
        React.createElement("div", { className: "nav-right" },
            React.createElement("a", { href: signInHref, className: "nav-account" },
                React.createElement(AccountIcon, null),
                React.createElement("span", null, accountLabel)),
            React.createElement("a", { href: cartHref, className: "nav-cart", "aria-label": `Cart, ${cartCount} item${cartCount === 1 ? '' : 's'}` },
                React.createElement(CartIcon, null),
                React.createElement("span", { className: `nav-cart-count ${cartCount > 0 ? 'has' : ''}` }, cartCount))),
        React.createElement("button", { className: `nav-burger ${menuOpen ? 'open' : ''}`, onClick: () => setMenuOpen(o => !o), "aria-label": "Menu" },
            React.createElement("span", null),
            React.createElement("span", null),
            React.createElement("span", null)),
        React.createElement("a", { href: cartHref, className: "nav-cart nav-cart-mobile", "aria-label": `Cart, ${cartCount} items` },
            React.createElement(CartIcon, null),
            React.createElement("span", { className: `nav-cart-count ${cartCount > 0 ? 'has' : ''}` }, cartCount)),
        React.createElement("div", { className: `nav-overlay ${menuOpen ? 'open' : ''}` },
            React.createElement("div", { className: "nav-overlay-links" },
                React.createElement("a", { href: shopHref, className: `nav-overlay-link ${shopActive ? 'active' : ''}`, onClick: () => setMenuOpen(false) }, "Shop"),
                navLinks.map(l => (React.createElement("a", { key: l.label, href: l.href, className: `nav-overlay-link ${activeHref === l.href ? 'active' : ''}`, onClick: () => setMenuOpen(false) }, l.label))),
                React.createElement("a", { href: signInHref, className: "nav-overlay-link nav-overlay-account", onClick: () => setMenuOpen(false) }, accountLabel),
                React.createElement("a", { href: shopHref, className: "btn btn-primary nav-overlay-shop", onClick: () => setMenuOpen(false) }, "Shop Companion Complex\u2122")))));
}
/* ---------- Check icon ---------- */
function CheckIcon() {
    return (React.createElement("svg", { viewBox: "0 0 12 12", fill: "none" },
        React.createElement("path", { d: "M2 6.2 L4.8 9 L10 3.2", stroke: "var(--black)", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })));
}
/* ---------- Stars ---------- */
function Stars({ count = 5, size = 14 }) {
    return (React.createElement("span", { style: { display: 'inline-flex', gap: 2 } }, Array.from({ length: count }).map((_, i) => (React.createElement("svg", { key: i, width: size, height: size, viewBox: "0 0 24 24", fill: "#D4A017" },
        React.createElement("path", { d: "M12 2l2.9 6.5 7.1.6-5.4 4.8 1.6 7-6.2-3.7-6.2 3.7 1.6-7L2 9.1l7.1-.6z" }))))));
}
/* ---------- Marquee (same items as V5) ---------- */
function Marquee() {
    const items = [
        'Clinician-Formulated', 'Electrolytes', 'Prebiotic Fiber', 'Creatine',
        'Collagen Peptides', 'Amino Acids', 'Vitamin C & Zinc',
        'No Artificial Sweeteners', 'Zero Sugar', 'Seamless Daily Supports',
        '30 Sticks Per Bag'
    ];
    const doubled = [...items, ...items];
    return (React.createElement("div", { className: "marquee" },
        React.createElement("div", { className: "marquee-track" }, doubled.map((it, i) => (React.createElement("span", { key: i, className: "marquee-item" },
            it,
            React.createElement("span", { className: "marquee-dot" }, "\u25C6")))))));
}
/* ---------- Footer (shared across pages) ---------- */
function Footer() {
    return (React.createElement("footer", { className: "footer" },
        React.createElement("div", { className: "footer-grid" },
            React.createElement("div", null,
                React.createElement(Logo, { cream: true, height: 36 }),
                React.createElement("p", { className: "body-md", style: { color: 'rgba(253,254,241,0.45)', marginTop: 18, maxWidth: 320, fontSize: 14 } }, "Companion Complex\u2122 is a clinician-formulated daily drink mix built for people on GLP-1s and peptides."),
                React.createElement("p", { style: { fontSize: 11, color: 'rgba(253,254,241,0.22)', lineHeight: 1.65, marginTop: 20, maxWidth: 320 } }, "*These statements have not been evaluated by the FDA. This product is not intended to diagnose, treat, cure, or prevent any disease.")),
            [
                ['Shop', [
                        ['Companion Complex™', L.shop],
                        ['Coconut Lime', L.coconutLime],
                        ['Passion Orange Guava', L.pog],
                        ['Subscribe & Save', L.shop],
                    ]],
                ['Learn', [
                        ['Our story', L.story],
                        ['The science', L.science],
                        ['For GLP-1 users', L.glp1],
                        ['For peptide users', L.peptides],
                        ['FAQ', L.faq],
                    ]],
                ['Support', [
                        ['Contact', L.contact],
                        ['Supplement Facts', L.supplementFacts],
                        ['Safety & allergen info', L.safetyAllergen],
                        ['Shipping & returns', L.shippingReturns],
                        ['Wholesale', L.wholesale],
                        ['Affiliates', L.affiliates],
                    ]],
                ['Account', [
                        ['Sign in', L.login],
                        ['Your orders', L.account],
                        ['Manage subscriptions', L.subscriptions],
                        ['Account hub', L.accountHub],
                    ]]
            ].
                map(([t, items]) => React.createElement("div", { key: t },
                React.createElement("h4", null, t),
                items.map(([label, href]) => React.createElement("a", { key: label, href: href }, label))))),
        React.createElement("div", { className: "footer-bottom" },
            React.createElement("span", { style: { fontSize: 12 } },
                "\u00A9 2026 Solia \u00B7 ",
                React.createElement("a", { href: L.privacy, style: { display: 'inline' } }, "Privacy"),
                " \u00B7 ",
                React.createElement("a", { href: L.terms, style: { display: 'inline' } }, "Terms")),
            React.createElement("span", { style: { fontSize: 13, fontStyle: 'italic', color: 'rgba(253,254,241,0.30)' } }, "Support, by the sip.\u2122"))));
}
/* ---------- export ---------- */
Object.assign(window, {
    useReveal, useScrolled, useScrollY, useElementProgress,
    clamp, lerp, range, ease,
    Logo, Nav, CheckIcon, Stars, Marquee, Footer, CartIcon, AccountIcon
});
